import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { portalApi, PORTAL_KEYS, PORTAL_PREVIEW_KEYS, isPortalPreview, clearPortalPreview } from '@/composables/usePortalApi';

/**
 * Client-portal auth store — completely separate from the staff `auth` store.
 * Tokens live under namespaced localStorage keys so the two sessions never clash.
 */
interface PortalUser {
  id: string;
  email: string;
  name: string | null;
  jobCount?: number;
}

export const usePortalAuthStore = defineStore('portalAuth', () => {
  const user = ref<PortalUser | null>(null);
  // A tab-scoped staff preview ("View as client") wins over a persisted client session.
  const isPreview = ref<boolean>(isPortalPreview());
  const tenantSlug = ref<string | null>(
    sessionStorage.getItem(PORTAL_PREVIEW_KEYS.slug) || localStorage.getItem(PORTAL_KEYS.slug),
  );
  const accessToken = ref<string | null>(
    sessionStorage.getItem(PORTAL_PREVIEW_KEYS.access) || localStorage.getItem(PORTAL_KEYS.access),
  );
  const refreshToken = ref<string | null>(localStorage.getItem(PORTAL_KEYS.refresh));

  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);

  /**
   * Enter a staff "View as client" preview: token lives ONLY in sessionStorage
   * (this tab), so a real client login in localStorage is untouched.
   */
  function enterPreview(slug: string, token: string) {
    sessionStorage.setItem(PORTAL_PREVIEW_KEYS.access, token);
    sessionStorage.setItem(PORTAL_PREVIEW_KEYS.slug, slug);
    isPreview.value = true;
    tenantSlug.value = slug;
    accessToken.value = token;
    user.value = null;
  }

  /** Leave the preview: clears this tab's preview session only. */
  function exitPreview() {
    clearPortalPreview();
    isPreview.value = false;
    user.value = null;
    accessToken.value = localStorage.getItem(PORTAL_KEYS.access);
    tenantSlug.value = localStorage.getItem(PORTAL_KEYS.slug);
  }

  function persist(slug: string, access: string, refresh: string) {
    localStorage.setItem(PORTAL_KEYS.slug, slug);
    localStorage.setItem(PORTAL_KEYS.access, access);
    localStorage.setItem(PORTAL_KEYS.refresh, refresh);
    tenantSlug.value = slug;
    accessToken.value = access;
    refreshToken.value = refresh;
  }

  function clear() {
    user.value = null;
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem(PORTAL_KEYS.access);
    localStorage.removeItem(PORTAL_KEYS.refresh);
  }

  async function login(slug: string, email: string, password: string) {
    const res = await portalApi.login(slug, email, password);
    persist(slug, res.accessToken, res.refreshToken);
    user.value = res.portalUser;
    return res;
  }

  async function acceptInvite(slug: string, token: string, password: string, name?: string) {
    const res = await portalApi.acceptInvite(slug, token, password, name);
    persist(slug, res.accessToken, res.refreshToken);
    user.value = res.portalUser;
    return res;
  }

  async function logout(): Promise<string | null> {
    const slug = tenantSlug.value;
    // Preview sessions: just drop the tab-scoped token — never revoke the real
    // client's refresh token that may be sitting in localStorage.
    if (isPreview.value) {
      exitPreview();
      return slug;
    }
    const rt = refreshToken.value;
    if (slug && rt) {
      try {
        await portalApi.logout(slug, rt);
      } catch {
        /* ignore */
      }
    }
    clear();
    return slug; // keep slug so the login page knows which workspace
  }

  async function restoreSession(): Promise<boolean> {
    if (!accessToken.value) return false;
    try {
      const me = await portalApi.me();
      user.value = me.user;
      if (me.tenant?.slug) tenantSlug.value = me.tenant.slug;
      return true;
    } catch {
      // A dead PREVIEW token must only end the preview — never nuke a real
      // client session persisted in localStorage.
      if (isPreview.value) {
        exitPreview();
      } else {
        clear();
      }
      return false;
    }
  }

  return {
    user,
    tenantSlug,
    accessToken,
    refreshToken,
    isAuthenticated,
    isPreview,
    enterPreview,
    exitPreview,
    login,
    acceptInvite,
    logout,
    restoreSession,
  };
});
